import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { X, Paperclip, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import SalesOrderViewModal from "./SalesOrderViewModal";
import { adminServices } from "../services/AdminServices";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const formatCurrency = (val) => {
  const n = Number(val || 0);
  return `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 0 })}`;
};

const normalizeItem = (item) => {
  if (!item || typeof item !== "object") return {};
  return {
    ...item,
    productId: item.productId ?? item.product_id ?? item.productID,
    sku: item.sku ?? item.productSku ?? item.product_sku ?? item.SKU,
    description: item.description ?? item.productName ?? item.product_name ?? item.name ?? item.product ?? item.itemName,
    uomName: item.uomName ?? item.uom_name ?? item.UomName ?? item.uom ?? item.unitOfMeasure ?? item.unit,
    quantity: item.quantity ?? item.qty ?? item.poQty ?? item.po_quantity ?? item.orderQty,
    unitPrice: item.unitPrice ?? item.price ?? item.cost ?? item.unit_price,
  };
};

const MIN_ROWS = 10;

const EditSalesOrderModal = ({ isOpen, onClose, onSaveDraft, onSubmit, selectedDN, salesOrderId, orderDetails }) => {
  const [processedBy, setProcessedBy] = useState("");
  const [certifiedCorrect, setCertifiedCorrect] = useState("");
  const [receivedPayment, setReceivedPayment] = useState("");
  const [overallRemarks, setOverallRemarks] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const fileInputRef = useRef(null);

  const order = orderDetails?.order ?? {};
  const managerApproval = orderDetails?.managerApprovals?.[0] ?? {};
  const rawItems = selectedDN?.items ?? orderDetails?.purchaseOrders?.[0]?.items ?? managerApproval?.items ?? [];
  const soItems = rawItems.length > 0 ? rawItems.map(normalizeItem) : [];

  const dnLabel = selectedDN?.filename
    ? selectedDN.filename.replace(/\.pdf$/i, "")
    : "DN-26-01006A";
  const soNo = `SO-${dnLabel.replace(/^DN-/, "")}`;

  const totalQty = soItems.reduce((s, i) => s + Number(i.quantity ?? i.qty ?? 0), 0);
  const totalPrice = soItems.reduce((s, i) => s + Number(i.quantity ?? i.qty ?? 1) * Number(i.unitPrice ?? 0), 0);

  const isDirty =
    processedBy.trim() !== "" ||
    certifiedCorrect.trim() !== "" ||
    receivedPayment.trim() !== "" ||
    overallRemarks.trim() !== "" ||
    attachment !== null;

  useEffect(() => {
    if (salesOrderId && selectedDN) {
      setProcessedBy(selectedDN.processedBy || selectedDN.processed_by || "");
      setCertifiedCorrect(selectedDN.certifiedCorrect || selectedDN.certified_correct || "");
      setReceivedPayment(selectedDN.receivedPayment || selectedDN.received_payment || "");
      setOverallRemarks(selectedDN.remarks || "");
    } else {
      setProcessedBy("");
      setCertifiedCorrect("");
      setReceivedPayment("");
      setOverallRemarks("");
    }
    setAttachment(null);
  }, [salesOrderId, orderDetails, selectedDN]);

  useEffect(() => {
    if (!order.merchantId) {
      setMerchantDetails(null);
      return;
    }
    let cancelled = false;
    adminServices.getMerchantById(order.merchantId)
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        if (!cancelled) setMerchantDetails(data || null);
      })
      .catch(() => {
        if (!cancelled) setMerchantDetails(null);
      });
    return () => { cancelled = true; };
  }, [order.merchantId]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setAttachment(file);
    e.target.value = "";
  };

  const handleRemoveAttachment = () => setAttachment(null);


  const buildPayload = () => ({
    deliveryNoteId: selectedDN?.delivery_note_id ?? selectedDN?.deliveryNoteId ?? selectedDN?.id,
    salesOrderId,
    processedBy,
    certifiedCorrect,
    receivedPayment,
    remarks: overallRemarks,
    items: soItems.map((item) => ({
      ...item,
      productId: item.productId ?? item.product_id,
      quantity: item.quantity ?? item.qty ?? 0,
      unitPrice: item.unitPrice ?? 0,
    })),
  });

  const handleSaveDraft = async () => {
    const payload = buildPayload();
    if (!payload.deliveryNoteId && !payload.salesOrderId) {
      toast.error("Delivery Note ID is missing");
      return;
    }
    setSaving(true);
    try {
      await onSaveDraft?.(payload, attachment);
      onClose?.();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const payload = buildPayload();
    if (!payload.deliveryNoteId && !payload.salesOrderId) {
      toast.error("Delivery Note ID is missing");
      return;
    }
    setShowFinalizeModal(false);
    setSaving(true);
    try {
      await onSubmit?.(payload, attachment);
      setSnapshot({ processedBy, certifiedCorrect, receivedPayment, remarks: overallRemarks, attachment });
      setShowViewModal(true);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleCloseClick = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      onClose?.();
    }
  };

  const emptyCount = Math.max(0, MIN_ROWS - soItems.length);

  if (showViewModal) {
    return (
      <SalesOrderViewModal
        isOpen
        onClose={() => { setShowViewModal(false); onClose?.(); }}
        soItems={soItems}
        soNo={soNo}
        order={order}
        managerApproval={managerApproval}
        snapshot={snapshot}
      />
    );
  }

  return ReactDOM.createPortal(
    <div className="edn-modal-overlay" onClick={handleCloseClick}>

      {showFinalizeModal && (
        <div
          className="edn-confirm-overlay"
          onClick={(e) => { e.stopPropagation(); setShowFinalizeModal(false); }}
        >
          <div className="edn-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="edn-confirm-title">Finalize Sales Order</h3>
            <p className="edn-confirm-message">
              Are you sure you want to finalize this Sales Order? Once finalized, it can no longer be edited.
            </p>
            <button
              className="edn-confirm-btn edn-confirm-btn--primary"
              onClick={handleSubmit}
              disabled={saving}
              type="button"
            >
              {saving ? "Submitting…" : "Proceed"}
            </button>
            <button
              className="edn-confirm-btn edn-confirm-btn--ghost"
              onClick={() => setShowFinalizeModal(false)}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showUnsavedModal && (
        <div
          className="edn-confirm-overlay"
          onClick={(e) => { e.stopPropagation(); setShowUnsavedModal(false); }}
        >
          <div className="edn-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="edn-confirm-title">Unsaved Changes</h3>
            <p className="edn-confirm-message">
              You have unsaved changes. If you leave now, your changes will be lost.
            </p>
            <button
              className="edn-confirm-btn edn-confirm-btn--danger"
              onClick={() => { setShowUnsavedModal(false); onClose?.(); }}
              type="button"
            >
              Leave
            </button>
            <button
              className="edn-confirm-btn edn-confirm-btn--ghost"
              onClick={() => setShowUnsavedModal(false)}
              type="button"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      <div className="edn-modal-container" onClick={(e) => e.stopPropagation()}>

        <div className="edn-modal-header">
          <span className="edn-modal-header-title">Edit Sales Order</span>
          <button className="edn-modal-close-btn" onClick={handleCloseClick} aria-label="Close" type="button">
            <X size={20} />
          </button>
        </div>

        <div className="edn-modal-body">

          <div className="edn-doc-preview">
            <div className="eso-doc-header">
              <span className="eso-doc-title">Sales Order</span>
              <div className="edn-doc-meta-cell eso-doc-so-number">
                <span className="edn-doc-meta-label">SO No.:</span>
                <span className="edn-doc-meta-value">{soNo}</span>
              </div>
            </div>

            <div className="edn-doc-meta">
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Order Type:</span>
                  <span className="edn-doc-meta-value">{order.orderType ?? "Regular Order"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Date Issued:</span>
                  <span className="edn-doc-meta-value">{formatDate(new Date().toISOString())}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Merchant Name:</span>
                  <span className="edn-doc-meta-value">
                    {merchantDetails?.merchant_name ??
                      merchantDetails?.merchantName ??
                      merchantDetails?.name ??
                      order.merchant?.merchantName ??
                      order.merchant?.merchant_name ??
                      order.merchant?.name ??
                      order.merchantName ??
                      order.merchant_name ??
                      order.merchant?.id ??
                      order.merchantId ??
                      "-"}
                  </span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Store Code:</span>
                  <span className="edn-doc-meta-value">{merchantDetails?.merchantCode ?? order.merchant?.merchantCode ?? order.facility?.code ?? order.facilityCode ?? order.merchant?.id ?? order.merchantId ?? "-"}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Location:</span>
                  <span className="edn-doc-meta-value">{merchantDetails?.location ?? order.facility?.name ?? order.facilityName ?? order.colony?.colonyName ?? "-"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">PR No.:</span>
                  <span className="edn-doc-meta-value">{managerApproval.prNo ?? "-"}</span>
                </div>
              </div>
            </div>

            <div className="edn-doc-table-wrap">
              <table className="edn-doc-table">
                <thead>
                  <tr className="edn-doc-table-col-header">
                    <th>No.</th>
                    <th>SKU</th>
                    <th>Item Description</th>
                    <th>UOM</th>
                    <th>QTY</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {soItems.map((item, idx) => {
                    const qty = Number(item.quantity ?? item.qty ?? 0);
                    const price = Number(item.unitPrice ?? 0);
                    return (
                      <tr key={idx} className={idx % 2 !== 0 ? "edn-doc-table-row--alt" : ""}>
                        <td>{idx + 1}</td>
                        <td>{item.sku ?? item.productSku ?? "-"}</td>
                        <td>{item.description ?? item.productName ?? item.name ?? "-"}</td>
                        <td>{item.uomName ?? item.UomName ?? item.uom ?? "-"}</td>
                        <td>{qty}</td>
                        <td>{formatCurrency(price)}</td>
                        <td>{formatCurrency(qty * price)}</td>
                      </tr>
                    );
                  })}
                  {Array.from({ length: emptyCount }, (_, i) => (
                    <tr key={`empty-${i}`} className={(soItems.length + i) % 2 !== 0 ? "edn-doc-table-row--alt" : ""}>
                      <td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td>
                    </tr>
                  ))}
                  <tr className="eso-doc-table-total-row">
                    <td></td>
                    <td></td>
                    <td className="eso-doc-table-total-label">TOTAL</td>
                    <td></td>
                    <td>{totalQty}</td>
                    <td></td>
                    <td>{formatCurrency(totalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="edn-doc-sig-grid">
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Processed by</span>
                <span className="edn-doc-sig-value">Employee Name Here</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Noted by</span>
                <span className="edn-doc-sig-value">General Manager Name Here</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Certified Correct</span>
                <input
                  type="text"
                  className="eso-doc-sig-input"
                  placeholder="Name..."
                  value={certifiedCorrect}
                  onChange={(e) => setCertifiedCorrect(e.target.value)}
                />
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Received Payment</span>
                <input
                  type="text"
                  className="eso-doc-sig-input"
                  placeholder="Name..."
                  value={receivedPayment}
                  onChange={(e) => setReceivedPayment(e.target.value)}
                />
              </div>
            </div>

            <p className="edn-doc-page-footer">PAGE 1 OUT OF X</p>
          </div>

          <div className="edn-form edn-form--spaced">
            <div className="edn-form-row">
              <div className="edn-form-group">
                <label className="edn-form-label">Attachment</label>
                {attachment ? (
                  <div className="edn-attachment-chip">
                    <Paperclip size={14} />
                    <span className="edn-attachment-name" title={attachment.name}>
                      {attachment.name.length > 22 ? attachment.name.slice(0, 19) + "..." : attachment.name}
                    </span>
                    <span className="edn-attachment-full">{attachment.name}</span>
                    <button
                      className="edn-attachment-action"
                      onClick={() => fileInputRef.current?.click()}
                      title="Replace file"
                      type="button"
                    >
                      <RefreshCw size={14} />
                    </button>
                    <button
                      className="edn-attachment-action edn-attachment-delete"
                      onClick={handleRemoveAttachment}
                      title="Remove"
                      type="button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    className="edn-attachment-empty"
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                  >
                    <Paperclip size={14} />
                    Attach file
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>

              <div className="edn-form-group">
                <label className="edn-form-label">Processed by</label>
                <input
                  type="text"
                  className="edn-form-input"
                  placeholder="Employee Name"
                  value={processedBy}
                  onChange={(e) => setProcessedBy(e.target.value)}
                />
              </div>
            </div>

            <div className="edn-form-group">
              <label className="edn-form-label">Remarks</label>
              <textarea
                className="edn-form-textarea"
                rows={3}
                value={overallRemarks}
                onChange={(e) => setOverallRemarks(e.target.value)}
              />
            </div>
          </div>

          <div className="edn-page-footer">
            <button
              className="edn-btn-draft"
              onClick={handleSaveDraft}
              disabled={saving}
              type="button"
            >
              Save as Draft
            </button>
            <button
              className="edn-btn-submit"
              onClick={() => setShowFinalizeModal(true)}
              disabled={saving}
              type="button"
            >
              Finalize Sales Order
            </button>
          </div>

        </div>

      </div>

    </div>,
    document.body
  );
};

export default EditSalesOrderModal;
